import React from "react";
import moment from "moment";
import * as Mui from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import { getStatusFlow } from "services/constants";
import {
  designColors,
  designFontFamily,
} from "services/constants/designTokens";

const statusColor = (status) => {
  if (status === "approved" || status === "completed") return "#0BB783";
  if (status === "applied") return "#0BB783";
  if (status === "pending") return "#C19A6B";
  if (status === "rejected" || status === "rejectReason") return "#F64E60";
  if (status === "cancelled" || status === "cancelReason") return "#6B7280";
  return "#94A3B8";
};

const statusTitle = (status, requestedLabel = "Requested") => {
  const map = {
    applied: requestedLabel,
    pending: "Pending",
    approved: "Approved",
    completed: "Completed",
    rejected: "Rejected",
    cancelled: "Cancelled",
    rejectReason: "Reason for Rejection",
    cancelReason: "Reason for Cancellation",
  };
  return map[status] || status || "-";
};

const DATE_FORMATS = [
  "DD-MM-YYYY, HH:mm",
  "DD-MM-YYYY HH:mm",
  "DD-MM-YYYY",
  "YYYY-MM-DD",
  "DD/MM/YYYY",
  "YYYY/MM/DD",
  "DD MMM YYYY",
  moment.ISO_8601,
];

// True once every supplied date (a single value or a list -- the latest one
// wins) falls before today. Unparseable/missing dates never count as passed.
const hasDatePassed = (endDate) => {
  const values = (Array.isArray(endDate) ? endDate : [endDate]).filter(Boolean);
  const parsed = values
    .map((value) => moment(String(value), DATE_FORMATS, true))
    .filter((m) => m.isValid());
  if (!parsed.length) return false;
  return moment.max(parsed).isBefore(moment().startOf("day"));
};

const resolveSteps = (data) => {
  const flow = getStatusFlow(data);
  const status = String(data?.status || "");
  const statusKey =
    Object.keys(flow).find(
      (key) => key.toLowerCase() === status.toLowerCase(),
    ) || status;
  return flow[statusKey] || [];
};

const buildStepperSteps = (data, steps, forceCompleted, hidePendingStep) => {
  const byStatus = steps.reduce((acc, step) => {
    acc[step.status] = step;
    return acc;
  }, {});
  const status = String(data?.status || "").toLowerCase();
  const applied = byStatus.applied || steps[0] || {};
  const pending = {
    ...(byStatus.pending || {}),
    status: "pending",
    date: byStatus.pending?.date || data?.pending_at || data?.pending_on,
  };
  const approved = {
    ...(byStatus.approved || {}),
    status: "approved",
    date: byStatus.approved?.date || data?.approved_at || data?.approved_on,
    user:
      byStatus.approved?.user ||
      data?.approved_by ||
      (["approved", "completed"].includes(status) ? data?.approver_name : undefined),
  };
  const terminalStatus =
    status === "rejected"
      ? "rejected"
      : status === "cancelled" || status === "canceled"
        ? "cancelled"
        : "approved";
  const terminal = {
    ...(byStatus[terminalStatus] || {}),
    status: terminalStatus,
    // Same "prefer the derived step, fall back to a *_by/*_at key on the raw
    // record" pattern the approved step above already uses -- without this,
    // a caller whose getStatusFlow() entry came back without a user/date
    // (e.g. cancelled_by missing from that specific payload) silently drops
    // "by <name>" instead of falling back to the raw field.
    user:
      byStatus[terminalStatus]?.user || data?.[`${terminalStatus}_by`] || undefined,
    date:
      byStatus[terminalStatus]?.date || data?.[`${terminalStatus}_at`] || undefined,
  };
  const completed = {
    ...(byStatus.completed || {}),
    status: "completed",
  };
  // approver_name (and any bare "who's assigned" field) gets pre-stamped at
  // apply time, before anyone has actually clicked Approve -- it names the
  // assigned approver, not proof of approval. Only a real approved_at/
  // byStatus.approved.date timestamp is evidence an approval genuinely
  // happened before this record was cancelled; a name alone (approved_by/
  // approver_name with no date) is exactly the shape that field takes for a
  // request cancelled straight from Pending, so it must not be trusted here.
  const hasApprovedBeforeCancel =
    terminalStatus === "cancelled" &&
    Boolean(byStatus.approved?.date || data?.approved_at || data?.approved_on);

  if (terminalStatus === "approved") {
    completed.state =
      status === "completed" || forceCompleted ? "done" : "upcoming";
  }

  const lifecycle = [
    { ...applied, status: "applied" },
    pending,
    ...(hasApprovedBeforeCancel ? [approved] : []),
    terminal,
    ...(terminalStatus === "approved" ? [completed] : []),
  ];
  const currentIndex = lifecycle.findIndex((step) => step.status === status);

  const mapped = lifecycle.map((step, index) => ({
    ...step,
    state:
      index < currentIndex || status === "completed"
        ? "done"
        : index === currentIndex
          ? "current"
          : step.state || "upcoming",
  }));

  return hidePendingStep
    ? mapped.filter((step) => step.status !== "pending")
    : mapped;
};

const StepperItem = ({ step, nextStep, index, count, requestedLabel }) => {
  const isUpcoming = step.state === "upcoming";
  const nextIsUpcoming = nextStep?.state === "upcoming";
  const color = isUpcoming ? designColors.slate400 : statusColor(step.status);
  const hasConnector = index < count - 1;

  return (
    <Mui.Box
      sx={{ position: "relative", flex: 1, minWidth: 0, textAlign: "center" }}
    >
      {hasConnector ? (
        <Mui.Box
          sx={{
            position: "absolute",
            display: { xs: "none", sm: "block" },
            top: 14,
            left: "calc(50% + 20px)",
            right: "calc(-50% + 20px)",
            borderTop: `2px ${nextIsUpcoming ? "dashed" : "solid"} ${
              nextIsUpcoming ? designColors.slate300 : "#6FD3B2"
            }`,
            zIndex: 0,
          }}
        />
      ) : null}
      <Mui.Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: 30,
          height: 30,
          mx: "auto",
          borderRadius: "50%",
          backgroundColor: isUpcoming ? designColors.white : color,
          border: `2px solid ${color}`,
          color: isUpcoming ? color : designColors.white,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: isUpcoming
            ? "none"
            : "0 4px 12px rgba(15, 118, 110, 0.16)",
        }}
      >
        {isUpcoming ? (
          <RadioButtonUncheckedIcon sx={{ fontSize: 17 }} />
        ) : (
          <CheckCircleIcon sx={{ fontSize: 17 }} />
        )}
      </Mui.Box>
      <Mui.Typography
        sx={{
          mt: 0.85,
          fontSize: 12.5,
          fontWeight: 700,
          color: designColors.slate800,
          lineHeight: 1.2,
        }}
      >
        {statusTitle(step.status, requestedLabel)}
      </Mui.Typography>
      <Mui.Typography
        title={
          [step.date, step.user].filter(Boolean).join(" by ") ||
          step.helper ||
          "-"
        }
        sx={{
          mt: 0.35,
          fontSize: 11.5,
          color: designColors.slate600,
          lineHeight: 1.35,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {step.date || step.helper || "-"}
      </Mui.Typography>
      {step.user ? (
        <Mui.Typography
          sx={{
            mt: 0.2,
            fontSize: 11.5,
            color: designColors.slate700,
            lineHeight: 1.35,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          by {step.user}
        </Mui.Typography>
      ) : null}
    </Mui.Box>
  );
};

// "REQUEST STATUS" section for the redesigned view panels. The horizontal
// stepper is the default shared layout; callers can still opt into the legacy
// list layout with variant="list" when needed.
export const RequestStatusTimeline = ({
  data,
  variant = "stepper",
  forceCompleted = false,
  requestedLabel = "Requested",
  hidePendingStep = false,
  // The request's (last) scheduled date. Once it has passed on an Approved
  // request, the flow is shown as Completed.
  endDate,
}) => {
  const steps = resolveSteps(data);
  if (!steps.length) return null;

  if (variant === "stepper") {
    const stepperSteps = buildStepperSteps(
      data,
      steps,
      forceCompleted ||
        (String(data?.status || "").toLowerCase() === "approved" &&
          hasDatePassed(endDate)),
      hidePendingStep,
    );
    const noteSteps = steps.filter((step) =>
      ["rejectReason", "cancelReason"].includes(step.status),
    );

    return (
      <Mui.Box
        sx={{
          p: 2,
          border: `1px solid ${designColors.slate200}`,
          borderRadius: 2,
          backgroundColor: designColors.white,
        }}
      >
        <Mui.Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: designColors.slate500,
            fontFamily: designFontFamily,
            mb: 2,
          }}
        >
          Request Status
        </Mui.Typography>
        <Mui.Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1.75, sm: 0 }}
          sx={{ px: { xs: 0, sm: 0.5 } }}
        >
          {stepperSteps.map((step, index) => (
            <StepperItem
              key={`${step.status}-${index}`}
              step={step}
              nextStep={stepperSteps[index + 1]}
              index={index}
              count={stepperSteps.length}
              requestedLabel={requestedLabel}
            />
          ))}
        </Mui.Stack>
        {noteSteps.length ? (
          <Mui.Stack
            spacing={0.75}
            sx={{
              mt: 2,
              pt: 1.25,
              borderTop: `1px solid ${designColors.slate100}`,
            }}
          >
            {noteSteps.map((step, index) => (
              <Mui.Typography
                key={`${step.status}-${index}`}
                sx={{
                  fontSize: 12.5,
                  color: designColors.slate700,
                  lineHeight: 1.45,
                }}
              >
                <Mui.Box component="span" sx={{ fontWeight: 700 }}>
                  {statusTitle(step.status)}:
                </Mui.Box>{" "}
                {step.user || "-"}
              </Mui.Typography>
            ))}
          </Mui.Stack>
        ) : null}
        <ApprovalAuditTrail record={data} />
      </Mui.Box>
    );
  }

  return (
    <Mui.Box
      sx={{
        p: 2,
        border: `1px solid ${designColors.slate200}`,
        borderRadius: 2,
        backgroundColor: designColors.white,
      }}
    >
      <Mui.Typography
        sx={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: designColors.slate500,
          fontFamily: designFontFamily,
          mb: 1.5,
        }}
      >
        Request Status
      </Mui.Typography>
      {steps.map((step, index) => (
        <StatusRow
          key={`${step.status}-${index}`}
          status={step.status}
          user={step.user}
          date={step.date}
          showConnector={index < steps.length - 1}
        />
      ))}
      <ApprovalAuditTrail record={data} />
    </Mui.Box>
  );
};

export default RequestStatusTimeline;
